import { FilterRequestBody, FilterValuesResponse, MultiselectOption, ReportMetric, Req } from '@cbo/shared-library';
import { Sales as SalesRequestTypes } from '@cbo/shared-library/request';
import { Calendar, Sales as SalesResponses } from '@cbo/shared-library/response';
import axios, { AxiosResponse } from 'axios';
import { Buffer } from 'buffer';
import { Dictionary } from 'lodash';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { BaseGridRow } from '../../shared/components/DataGrid/DataGrid';
import { useSnackbar } from '../../shared/contexts/SnackbarContext';
import { useUsers } from '../../shared/contexts/userContext';
import { IApiError } from '../../shared/services/types';
import useCurrentSite from '../../shared/utils/hooks/useCurrentSite';
import useCallBsl from '../../shared/utils/hooks/useCallBsl';
import SalesUtilities from '../../sales/utilities';
import { Frequency } from '../../sales/Reports/JournalEntry/JournalEntryPage';
import { ExportFileReportGridResponse } from '../../shared/labor/types';
import { downloadFile } from '../../shared/labor/utilities/laborUtilities';
import { StatementBodySchema } from './mutations';
import {
  CreateStatementPDFBodySchema,
  CreateTransactionBodySchema,
  HouseAccountConsumer,
  HouseAccountSite,
} from '../../sales/HouseAccounts/types';
import { BodyOverflowError, CreateTransactionPDFBodySchema } from '../../sales/types';

export const proxies = {
  houseAccounts: 'house-accounts',
  calendar: 'calendar',
  employeeConfiguration: 'employee-configuration',
  sales: 'sales',
  glAccounts: 'gl-accounts',
  siteManagement: 'site-management',
};
const Verb = Req.Firebase.HttpVerb;

export interface Id {
  id: number;
}
export type HouseAccountResponseSchema = {
  houseAccountId: string;
  houseAccountName: string;
};

export type HouseAccountRequestSchema = {
  contactEmail: string;
  isActive: boolean;
  maxBalance: number | null;
  houseAccountName: string;
  contactFirstName: string;
  contactLastName: string;
  contactPhoneNumber: string;
  address: string | null;
  address2: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  postalCode: string | null;
  consumers: {
    consumerId: string;
  }[];
  sites: {
    siteId: string;
  }[];
};
export interface BaseReportDataRequest {
  startDate: string;
  endDate: string;
  siteIds: string[];
}

export interface BaseReportComparisonDataRequest extends BaseReportDataRequest {
  previousStartDate: string;
  previousEndDate: string;
}

export interface ComparativeSalesLaborPdfExportRequest extends BaseReportComparisonDataRequest {
  asJSON: boolean;
  showVisualization: boolean;
}

export interface ComparativeSalesLaborCsvExportRequest extends BaseReportComparisonDataRequest {
  filterQuery?: string;
}

export interface HouseAccountSummary {
  houseAccountId: string;
  houseAccountName: string;
  isActive: boolean;
  customerId: string;
  lastTransactionAt: Date | null;
  lastStatementSentAt: Date | null;
  siteName: string | null;
  siteCount: number;
  deletedAt: Date | null;
  balance: string;
}

export interface HouseAccountSummaryPage {
  result: HouseAccountSummary[];
  count: number;
  pageNumber: number;
  rowsPerPage: number;
}

export interface ConsumerInfo {
  name: string;
  phone: string;
}

export type UpdateHouseAccountRequestSchema = {
  isActive: boolean;
};

export interface HouseAccountActivity {
  activityId: string;
  activityType: string;
  amount: string;
  balance: string | null;
  createdAt: Date;
  employeeId: string | null;
  employeeName: string | null;
  houseAccountId: string;
  number: number | null;
  siteId: string | null;
  siteName: string | null;
  statementSentAt: Date | null;
  transactionTypeLabel: string | null;
  ticketNumber: string | null;
  statementNumber: number | null;
  consumerInfo: ConsumerInfo | null;
}

export interface HouseAccountActivitiesPage {
  result: (HouseAccountActivity & { consumerInfo: string })[];
  count: number;
  pageNumber: number;
  rowsPerPage: number;
}

export interface HouseAccount {
  houseAccountId: string;
  houseAccountName: string;
  houseAccountNumber: number;
  balance: string;
  isActive: boolean;
  contactFirstName: string;
  contactLastName: string;
  contactEmail: string;
  contactPhoneNumber: string;
  address: string;
  address2: string;
  city: string;
  state: string;
  country: string;
  postalCode: string;
  maxBalance: number;
  deletedAt: Date;
  sites: HouseAccountSite[];
  consumers: HouseAccountConsumer[];
}

export interface TransactionSummary {
  transactionDate: Date;
  type: string;
  total: string;
  amount: string;
  tax: string;
  tip: string;
  gratuity: string;
  siteName: string;
  receiptId: string;
}

export interface StatementSchemaBase {
  statementDate: Date;
  statementNumber: number;
  billingPeriodStartDate: Date;
  billingPeriodEndDate: Date;
  billerName: string;
  billerAddress: string;
  billerAddress2: string;
  billerCity: string;
  billerState: string;
  billerCountry: string;
  billerPostalCode: string;
  houseAccountNumber: number;
  houseAccountName: string;
  contactFirstName: string;
  contactLastName: string;
  contactAddress: string;
  contactAddress2: string;
  contactCity: string;
  contactState: string;
  contactCountry: string;
  contactPostalCode: string;
  lastPaymentDate: Date;
  lastPaymentAmount: string;
  amountDue: string;
  balanceForward: string;
  totalBalance: string;
  transactions: TransactionSummary[];
}

export interface HouseAccountStatement extends StatementSchemaBase {
  statementId: string;
  statementSentAt: Date | null;
}

export interface Transaction {
  employeeName: string;
  transactionAmount: string;
  paymentMethod: string;
  notes: string;
  transactionId: string;
}

export interface TransactionItem {
  id: string;
  parentItemId?: string;
  productName: string;
  actualAmount: {
    amount: number;
  };
  quantity: {
    quantity: number;
  };
}

export interface TransactionTotals {
  totals: {
    grossAmount: {
      amount: number;
    };
    tipAmount?: {
      amount: number;
    };
    gratuityAmount?: {
      amount: number;
    };
    grandAmount: {
      amount: number;
    };
    taxExclusive: {
      amount: number;
    };
    taxInclusive: {
      amount: number;
    };
  };
  totalTaxes: [
    {
      name: string;
      amount: {
        amount: number;
      };
    }
  ];
}

export interface SaleTransaction {
  transactionId: string;
  externalId: string;
  tenderType: string;
  transactionType: string;
  transactionTypeLabel: string;
  transactionAmount: string;
  transactionDateTime: Date;
  employeeId: string;
  employeeName: string;
  transactionTotalsInfo: TransactionTotals;
  transactionItemsInfo: TransactionItem[];
  startingBalance: string;
  endingBalance: string;
  businessDay: Date;
  consumerId: string;
  consumerName: string;
  customerId: string;
  customerName: string;
  siteId: string;
  siteName: string;
  isDeleted: boolean;
  notes: string;
  ticketNumber: string;
  receiptId: string;
  paymentMethod: string;
  localCurrency: string;
  companyName: string;
  tableName: string;
  checkOpenedDate: Date;
  checkClosedDate: Date;
  guestCount: number;
}

export interface PdfResponse {
  data: string;
}

export interface GuestCheckResponse {
  siteId: string;
  siteName: string;
  transactionId: string;
  employee: string;
  employeeId: string;
  asmEmployeeGuid: string;
  customerName: string;
  tableName: string;
  terminalId: string;
  checkOpenTime: string;
  checkCloseTime: string;
  paymentName: string;
  amount: string;
  cardInformation: string;
  hasVoids: boolean;
  hasComps: boolean;
  hasPromos: boolean;
  hasRefunds: boolean;
}

export interface GuestCheckRequest extends BaseReportDataRequest {
  hasComps: string;
  hasPromos: string;
  hasRefunds: string;
  hasVoids: string;
}

export interface POSEventsRequest extends BaseReportDataRequest {
  employeeId: string[];
  terminalId: string[];
  eventType: string[];
}

export interface POSEventFiltersRequest {
  siteIds: string[];
}

export interface POSEventFiltersResponse {
  terminalId: MultiselectOption[];
  employee: MultiselectOption[];
}

export interface POSEventsResponse {
  siteId: string;
  siteName: string;
  employeeId: string;
  asmEmployeeGuid: string;
  employee: string;
  terminalId: string;
  eventTime: string;
  eventType: string;
  details: string;
}

export interface ParentGlAccount {
  accountNumber: number;
  accountId: string;
}

export interface GlAccount {
  accountNumber: number;
  accountId: string;
  accountName: string;
  accountType: string;
  accountTypeDetail: string;
  categoryId: number;
  categoryName: string;
  description: string;
  parentGLAccount?: ParentGlAccount;
  isActive: boolean;
  isDisplayedInInvoicing: boolean;
  subAccounts: GlAccount[];
}

export interface GlAccountsResponse {
  accounts: GlAccount[];
}

export interface GlAccountFiltersResponse {
  categoryInfo: {
    categoryId: number;
    categoryName: string;
  }[];
  accountTypes: string[];
  accountDetailTypes: string[];
}

export interface GlAccountInfo {
  accountNumber: number;
  accountName: string;
}

export interface GlAccountName {
  glAccountId: string;
  glAccountName: string;
}

export interface GlCategoryInfo {
  categoryId: number;
  categoryName: string;
}

export interface GlAccountUpsertInfoResponse {
  accountInfo: GlAccountInfo[];
  accountTypeInfo: Record<string, string[]>;
  categoryInfo: GlCategoryInfo[];
}

export interface GlAccountsRequest {
  isActive: string;
  categoryIds: number[];
  accountTypes: string[];
  accountDetailTypes: string[];
}

export interface CreateGlAccountRequest {
  accountNumber: number;
  accountName: string;
  categoryId: number;
  accountType?: string;
  accountTypeDetail?: string;
  parentAccountNumber?: number;
  description?: string;
  isActive: boolean;
  isDisplayedInInvoicing: boolean;
}

export interface CreateGlAccountsResponse {
  accountId: string;
}

export interface GlAccountsDeleteRequest {
  accountIds: string[];
}

export interface UpdateGlAccountRequest extends CreateGlAccountRequest {
  isActiveAffectedAccountIds?: string[];
}

export interface UpdateIsActiveGlAccountsRequest {
  isActive: boolean;
  isActiveAffectedAccountIds: string[];
}

export interface DashboardGraphDataRequest {
  startDate: string;
  endDate: string;
}

export interface DashboardGraphDataResponse {
  xAxis: string[];
  netSales: number[];
  comparisonNetSales: number[];
  forecastedNetSales: number[];
}

export interface KPIDataRequest {
  startDate: string;
  endDate: string;
  metricsRequested: ReportMetric[];
}

export interface SalesSummaryDataRequest {
  startDate: string;
  endDate: string;
  siteIds: string[];
}

export interface ProductMixMenuGridDataRequest {
  startDate: string;
  endDate: string;
  siteIds: string[];
  categories?: string[];
  items?: string[];
}

export interface ProductMixMenuGridDataExportRequest extends ProductMixMenuGridDataRequest {
  modifier: boolean;
  columns: string[];
}

export interface ProductMixTop5ItemsRequest {
  startDate: string;
  endDate: string;
  siteIds: string[];
  categories?: string[];
  items?: string[];
}

export interface ProductMixTotalSalesRequest {
  startDate: string;
  endDate: string;
  previousStartDate: string;
  previousEndDate: string;
  selectionType: string;
  siteIds: string[];
  categories?: string[];
  items?: string[];
}

export interface ProductMixEmployeeSalesDataRequest {
  startDate: string;
  endDate: string;
  siteIds: string[];
  categories?: string[];
  items?: string[];
  employees?: string[];
}

export interface RefundsGridDataRequest {
  startDate: string;
  endDate: string;
  siteIds: string[];
  employees?: string[];
}

export interface RefundsCardsDataRequest {
  startDate: string;
  endDate: string;
  previousStartDate: string;
  previousEndDate: string;
  siteIds: string[];
  employees?: string[];
}

export interface ReportMetricResult {
  currentValue: number;
  change: number;
}

export interface KPIDataResponse {
  isSuccess: boolean;
  reportMetricResults: Dictionary<ReportMetricResult>;
}

export interface ProductMixMenuGridResults extends BaseGridRow {
  hierarchy: string[];
  soldQuantity: number;
  modifierQuantity: number;
  itemSales: number;
  totalParentSales: number;
  percentOfCategory: number;
  percentOfParent: number;
  percentOfTotal: number;
  totalParentPercent: number;
  id: number;
}

export interface ProductMixItemResult {
  itemName: string;
  netSales: number;
  itemCount: number;
}

export interface ProductMixTotalSalesDataPoint {
  netSales: number;
  itemCount: number;
  dateLabel: string;
  date: string;
}

export interface ProductMixSellerResult {
  netSales: number;
  itemCount: number;
  employeeName: string;
}

export interface ProductMixEmployeeGridResults {
  hierarchy: string[];
  soldQuantity: number;
  itemSales: number;
  asmEmployeeGuid: string | null;
  id: number;
}
export interface ProductMixMenuGridDataResponse {
  results: ProductMixMenuGridResults[];
}
export interface ProductMixTop5ItemsResponse {
  items: ProductMixItemResult[];
}
export interface ProductMixTop5SellersResponse {
  topSellers: ProductMixSellerResult[];
}
export interface ProductMixTotalSalesResponse {
  selectedDateRange: ProductMixTotalSalesDataPoint[];
  comparisonDateRange: ProductMixTotalSalesDataPoint[];
}

export interface ProductMixEmployeeSalesGridResponse {
  results: ProductMixEmployeeGridResults[];
}

export interface SalesSummaryGridResults extends BaseGridRow {
  hierarchy: string[];
  id: number;
  quantity: number;
  total: number;
}

export interface SalesPaymentGridResults extends BaseGridRow {
  hierarchy: string[];
  id: number;
  quantity: number;
  amount: number;
  tipsGratuities: number;
  total: number;
}

export interface RefundDataGridResults extends BaseGridRow {
  id: number;
  businessDate: string;
  siteName: string;
  employeeName: string;
  employeeId: string | null;
  asmEmployeeGuid: string | null;
  receiptId: string;
  closeTimestamp: string;
  tenderType: string;
  refundAmount: number;
}

export interface SalesSummaryGridResponseDto {
  results: SalesSummaryGridResults[];
}
export interface SalesPaymentsGridResponseDto {
  results: SalesPaymentGridResults[];
}

export interface SalesSummaryDataResponse {
  netSales: number;
  discounts: number;
  charges: number;
  taxes: number;
  grossSales: number;
}

export interface RefundCardsDataResponse {
  refundedCount: number;
  refundAmount: number;
  refundCountComparisonPercent: number;
  refundAmountComparisonPercent: number;
}

export interface RefundDataGridResponse {
  results: RefundDataGridResults[];
}

export interface SalesDefinitionsSettings {
  organizationId: string;
  configurationName: string;
  includeExclusiveTaxes: boolean;
  includeCharges: boolean;
  includeVoids: boolean;
  includePromotions: boolean;
  includeDiscounts: boolean;
}

export interface UpdateSalesDefinitionsSettings {
  includeDiscounts?: boolean;
  includeExclusiveTaxes?: boolean;
  includeCharges?: boolean;
  includeVoids?: boolean;
  includePromotions?: boolean;
}

export interface UpdateSalesDefinitionsResponse {
  configurationName: string;
}
export interface VoidsReportCommonRequest {
  startDate: string;
  endDate: string;
  siteIds: string[];
  voidReasons?: string[];
  employees?: string[];
}

export interface VoidsReportGridResult extends BaseGridRow {
  dateOfBusiness: string;
  site: string;
  employeeName: string;
  employeeId: string | null;
  asmEmployeeGuid: string | null;
  approvedBy: string;
  transactionNumber: string;
  dateOrTime: string;
  voidReason: string;
  voidItem: string;
  voidCount: number;
  voidAmount: number;
  id: number;
}

export interface VoidsReportGridDataResponse {
  results: VoidsReportGridResult[];
}

export interface VoidReasonResult {
  voidReason: string;
  voidAmount: number;
  voidCount: number;
  percentOfTotal: number;
}

export interface VoidsReportTop5ReasonsDataResponse {
  voidReasons: VoidReasonResult[];
}

export interface VoidsReportKPICardsDataRequest extends VoidsReportCommonRequest {
  previousStartDate: string;
  previousEndDate: string;
}

export interface VoidsReportKPICardsDataResponse {
  totalVoidsCount: number;
  voidsCountPercentChange: number;
  totalVoidsAmount: number;
  voidsAmountPercentChange: number;
}

export interface TaxesRequestDto {
  startDate: string;
  endDate: string;
  siteIds: string[];
  taxNames?: string[];
}

export interface TotalTaxesResponse {
  totalTaxAmount: number;
}

export interface TaxesGridDataPoint extends BaseGridRow {
  taxName: string;
  id: number;
  taxType: string;
  taxRate: number;
  salesAmount: number;
  taxCollected: number;
}

export interface TaxesGridResponse {
  results: TaxesGridDataPoint[];
}

export interface TaxDataPoint {
  name: string;
  totalTaxAmount: number;
  percentOfTotalTax: number;
}

export interface TaxesByTypeResponse {
  results: TaxDataPoint[];
}

export interface DiscountReportCommonRequest {
  startDate: string;
  endDate: string;
  siteIds?: string[];
  discountNames?: string[];
  employees?: string[];
  approvedBy?: string[];
}

export interface DiscountReportGridDataRow extends BaseGridRow {
  id: number;
  dateOfBusiness: string;
  site: string;
  employeeName: string;
  employeeId: string | null;
  asmEmployeeGuid: string | null;
  approvedBy: string;
  transactionNumber: string;
  dateOrTime: string;
  discountType: string;
  discountName: string;
  discountCount: number;
  discountAmount: number;
}

export interface DiscountReportGridDataResponse {
  results: DiscountReportGridDataRow[];
}

export type PatchStatementBodySchema = {
  statementSentAt: Date | null;
};
export interface DiscountReportKPICardsRequest extends DiscountReportCommonRequest {
  previousStartDate: string;
  previousEndDate: string;
}

export interface DiscountReportKPICardsResponse {
  countOfDiscounts: number;
  discountCountPercentChange: number;
  totalAmountOfDiscounts: number;
  discountAmountPercentChange: number;
}

export interface DiscountItemData {
  discountName: string;
  discountAmount: number;
  numberOfDiscounts: number;
  percentOfTotal: number;
}

export interface DiscountReportTop5DiscountsResponse {
  discountItems: DiscountItemData[];
}

export interface RevenueCenterCommonRequest {
  startDate: string;
  endDate: string;
  siteIds: string[];
  dayparts?: string[];
  revenueCenters?: string[];
}

export interface PerPersonAverageByRevenueCenterResults {
  revenueCenter: string;
  netSales: number;
  perPersonAverage: number;
  guestCount: number;
}

export interface PerPersonAverageByRevenueCenterResponse {
  results: PerPersonAverageByRevenueCenterResults[];
}

export interface SalesByRevenueCenterResults {
  revenueCenter: string;
  netSales: number;
  transactionCount: number;
  guestCount: number;
}

export interface SalesByRevenueCenterResponse {
  results: SalesByRevenueCenterResults[];
}

export interface RevenueCenterGridResults extends BaseGridRow {
  id: number;
  hierarchy: string[];
  totalNetSales: number;
  totalTaxAmount: number;
  totalDiscountAmount: number;
  totalSurchargeAmount: number;
  totalRefundsAmount: number;
  totalGrossSales: number;
  transactionCount: number;
  totalGuestCount: number;
  perPersonAverage: number;
  transactionAverage: number;
}

export interface RevenueCenterGridResponse {
  results: RevenueCenterGridResults[];
}

export interface ComparativeSalesDataGridResults extends BaseGridRow {
  id: string | number;
  siteId: string | null;
  siteName: string;
  businessDate: string | null;
  compBusinessDate: string | null;
  isSubtotal?: boolean;
  isTotal?: boolean;
  netSalesAmount: number;
  compNetSalesAmount: number;
  salesVariance: number;
  noOfGuests: number;
  noOfChecks: number;
  laborCost: number;
  actualLaborHours: number;
  laborPercent: number;
  salesPerLaborHour: number;
  checkAverage: number;
  perPersonAverage: number;
}

export interface TopBottomSiteData {
  siteId: string;
  siteName: string;
  netSalesAmount: number;
  salesVariance: number;
}

export interface TopBottomSalesPerLaborHourData {
  siteId: string;
  siteName: string;
  salesPerLaborHour: number;
  salesPerLaborHourVariancePercentage: number;
}

export interface ComparativeSalesCardResults {
  totalNetSales: number;
  totalLaborCost: number;
  totalNetSalesVariance: number;
  totalLaborCostVariance: number;
  top5NetSales: TopBottomSiteData[];
  bottom5NetSales: TopBottomSiteData[];
  top5SalesPerLaborHour: TopBottomSalesPerLaborHourData[];
  bottom5SalesPerLaborHour: TopBottomSalesPerLaborHourData[];
}

export interface ProfitLossGridDataRequest {
  startDate: string;
  endDate: string;
  comparisonStartDate: string;
  comparisonEndDate: string;
  siteIds: string[];
}

export interface AmountAndPercentage {
  amount: number;
  percentOfSales: number;
}

export interface ProfitLossGridData extends BaseGridRow {
  hierarchy: string[];
  id: number;
  currentPeriodTotal: number;
  currentPeriodPercentage: number;
  comparisonPeriodTotal: number;
  comparisonPeriodPercentage: number;
}

export interface ProfitLossPageResponse {
  results: ProfitLossGridData[];
  sales: AmountAndPercentage;
  costOfGoods: AmountAndPercentage;
  costOfLabor: AmountAndPercentage;
  grossMargin: AmountAndPercentage;
}

export interface PaymentsByTypeResults {
  transactionCount: number;
  paymentAmount: number;
  paymentAmountPercentage: number;
  paymentName: string;
}

export interface PaymentsByTypeResponseDto {
  results: PaymentsByTypeResults[];
}

export interface PaymentsGridResults extends BaseGridRow {
  dateOfBusiness: string;
  id: number;
  siteName: string;
  employee: string;
  receiptId: string;
  transactionId: string;
  timestamp: string;
  paymentType: string;
  paymentName: string;
  paymentAmount: number;
  tipAmount: number;
}

export interface PaymentsGridResponseDto {
  results: PaymentsGridResults[];
}

export interface PaymentsRequestDto extends BaseReportDataRequest {
  paymentTypes?: string[];
  employees?: string[];
}

export interface PaymentsComparisonRequestDto extends BaseReportComparisonDataRequest {
  paymentTypes?: string[];
  employees?: string[];
}

export interface TotalPaymentsResponseDto {
  paymentsAmount: number;
  comparisonPaymentsAmount: number;
  amountComparisonPercentage: number;
}

export interface TotalSalesByShiftResponseDto {
  employeeId?: string;
  posEmployeeId?: string;
  netTotalSales: number;
  shiftId: string;
}

export interface JournalEntryId {
  journalEntryId: string;
}

export interface RecurringJournalEntryId {
  recurringJournalEntryId: string;
}

export interface GetOneJournalEntryResponse {
  journalEntryId: string;
  siteId: string;
  businessDate?: string;
  glAccountName: string;
  glAccountId: string;
  frequency: Frequency;
  amount: number;
  createdBy: string;
  isCredit: boolean;
  description?: string;
  startDate?: string;
  endDate?: string;
  isActive?: boolean;
}

export interface GetAllJournalEntryRequest extends BaseReportDataRequest {
  glAccountIds?: string[];
  frequencyTypes?: string[];
}

export interface GetAllJournalEntryResponse {
  journalEntries: GetOneJournalEntryResponse[];
}

export interface CreateJournalEntryRequest {
  siteId: string;
  glAccountId: string;
  isCredit: boolean;
  amount: number;
  frequency?: Frequency;
  businessDate?: string;
  description?: string;
  startDate?: string;
  endDate?: string;
}

export type SalesRequests = {
  getOneJournalEntry: (id: string) => Promise<GetOneJournalEntryResponse | undefined>;
  getAllJournalEntries: (body: GetAllJournalEntryRequest) => Promise<GetAllJournalEntryResponse>;
  createJournalEntry: (body: CreateJournalEntryRequest) => Promise<JournalEntryId | undefined>;
  updateJournalEntry: (body: Partial<CreateJournalEntryRequest>, id: string) => Promise<JournalEntryId | undefined>;
  deleteJournalEntry: (id: string) => Promise<JournalEntryId | undefined>;
  getOneRecurringJournalEntry: (id: string) => Promise<GetOneJournalEntryResponse | undefined>;
  getAllRecurringJournalEntries: (body: GetAllJournalEntryRequest) => Promise<GetAllJournalEntryResponse>;
  createRecurringJournalEntry: (body: CreateJournalEntryRequest) => Promise<RecurringJournalEntryId | undefined>;
  updateRecurringJournalEntry: (
    body: Partial<CreateJournalEntryRequest>,
    id: string
  ) => Promise<RecurringJournalEntryId | undefined>;
  deleteRecurringJournalEntry: (id: string) => Promise<RecurringJournalEntryId | undefined>;
  getHouseAccount: (houseAccountId: string) => Promise<HouseAccount | undefined>;
  getHouseAccountsSummary: () => Promise<HouseAccountSummary[]>;
  createHouseAccount: (body: HouseAccountRequestSchema) => Promise<HouseAccountResponseSchema | undefined>;
  getHouseAccountsActivities: (houseAccountId: string) => Promise<HouseAccountActivity[]>;
  putHouseAccount: (houseAccountId: string, body: HouseAccountRequestSchema) => Promise<HouseAccount | undefined>;
  getStatement: (statementId: string) => Promise<HouseAccountStatement>;
  createStatement: (houseAccountId: string, body: StatementBodySchema) => Promise<HouseAccountStatement | undefined>;
  createStatementPDF: (body: CreateStatementPDFBodySchema) => Promise<string>;
  deleteStatement: (statementId: string) => Promise<boolean>;
  patchStatement: (statementId: string, body: PatchStatementBodySchema) => Promise<boolean>;
  patchHouseAccount: (houseAccountId: string, body: UpdateHouseAccountRequestSchema) => Promise<boolean>;
  deleteHouseAccount: (houseAccountId: string) => Promise<boolean>;
  restoreHouseAccount: (houseAccountId: string) => Promise<boolean>;
  createTransaction: (houseAccountId: string, body: CreateTransactionBodySchema) => Promise<Transaction>;
  getTransaction: (transactionId: string) => Promise<Transaction | undefined>;
  getSaleTransaction: (transactionId: string) => Promise<SaleTransaction | undefined>;
  getGuestTransaction: (
    transactionId: string,
    businessDate: string
  ) => Promise<SalesResponses.GetGuestTransactionDetailsResponseDto | undefined>;
  createPdf: (
    body: CreateTransactionPDFBodySchema | SalesRequestTypes.TransactionPdfDto
  ) => Promise<string | undefined>;
  getKPIData: (body: KPIDataRequest) => Promise<KPIDataResponse>;
  getSalesSummaryCardData: (body: SalesSummaryDataRequest) => Promise<SalesSummaryDataResponse>;
  getDashboardGraphData: (body: DashboardGraphDataRequest) => Promise<DashboardGraphDataResponse>;
  getKPIData2: (body: KPIDataRequest) => Promise<KPIDataResponse>;
  getCalendarFilters: () => Promise<Calendar.FiscalCalendarFilters | undefined>;
  getAllGuestChecks: (body: GuestCheckRequest) => Promise<GuestCheckResponse[]>;
  exportAllGuestChecks: (body: GuestCheckRequest) => Promise<ExportFileReportGridResponse | void>;
  getPOSEventFilters: (body: POSEventFiltersRequest) => Promise<POSEventFiltersResponse>;
  getPOSEvents: (body: POSEventsRequest) => Promise<POSEventsResponse[]>;
  getProductMixMenuGridData: (body: ProductMixMenuGridDataRequest) => Promise<ProductMixMenuGridDataResponse>;
  exportProductMixMenuGridData: (
    body: ProductMixMenuGridDataExportRequest
  ) => Promise<ExportFileReportGridResponse | void>;
  getProductMixTop5Items: (body: ProductMixTop5ItemsRequest) => Promise<ProductMixTop5ItemsResponse>;
  getProductMixTotalSales: (body: ProductMixTotalSalesRequest) => Promise<ProductMixTotalSalesResponse>;
  getProductMixTop5Sellers: (body: ProductMixEmployeeSalesDataRequest) => Promise<ProductMixTop5SellersResponse>;
  getProductMixEmployeeSalesGrid: (
    body: ProductMixEmployeeSalesDataRequest
  ) => Promise<ProductMixEmployeeSalesGridResponse>;
  exportProductMixEmployeeSalesGridData: (
    body: ProductMixEmployeeSalesDataRequest
  ) => Promise<ExportFileReportGridResponse | void>;
  getFilterValues: (body: FilterRequestBody) => Promise<FilterValuesResponse>;
  getGlAccounts: (body: GlAccountsRequest) => Promise<GlAccountsResponse | undefined>;
  getGlAccountFilters: () => Promise<GlAccountFiltersResponse | undefined>;
  getGlAccountUpsertInfo: () => Promise<GlAccountUpsertInfoResponse | undefined>;
  createGlAccount: (body: CreateGlAccountRequest) => Promise<CreateGlAccountsResponse>;
  deleteGlAccount: (body: GlAccountsDeleteRequest) => Promise<boolean>;
  updateGlAccount: (glAccountId: string, body: UpdateGlAccountRequest) => Promise<boolean>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  uploadGlAccounts: (file: File) => Promise<AxiosResponse<any, any>>;
  restoreDeleteGlAccount: (body: GlAccountsDeleteRequest) => Promise<boolean>;
  updateIsActiveGlAccounts: (body: UpdateIsActiveGlAccountsRequest) => Promise<boolean>;
  getAllGlAccountNames: (categoryIds?: number[]) => Promise<GlAccountName[] | undefined>;
  getGlAccountById: (glAccountId: string) => Promise<GlAccount | void>;
  getSalesDefinitionsSettings: () => Promise<SalesDefinitionsSettings>;
  updateSalesDefinitionsSettings: (body: UpdateSalesDefinitionsSettings) => Promise<UpdateSalesDefinitionsResponse>;
  getComparativeSalesLaborGridData: (
    body: BaseReportComparisonDataRequest
  ) => Promise<ComparativeSalesDataGridResults[]>;
  getComparativeSalesLaborCardsData: (body: BaseReportComparisonDataRequest) => Promise<ComparativeSalesCardResults>;
  exportComparativeSalesLaborPdf: (body: ComparativeSalesLaborPdfExportRequest) => Promise<string | undefined>;
  exportComparativeSalesLaborCsv: (
    body: ComparativeSalesLaborCsvExportRequest
  ) => Promise<ExportFileReportGridResponse | void>;
  getSalesSummaryGridData: (body: SalesSummaryDataRequest) => Promise<SalesSummaryGridResponseDto>;
  exportSalesSummaryGridData: (body: SalesSummaryDataRequest) => Promise<ExportFileReportGridResponse | void>;
  getSalesSummaryPaymentsGridData: (body: SalesSummaryDataRequest) => Promise<SalesPaymentsGridResponseDto>;
  exportSalesSummaryPaymentsGridData: (body: SalesSummaryDataRequest) => Promise<ExportFileReportGridResponse | void>;
  getProfitLossGridData: (body: ProfitLossGridDataRequest) => Promise<ProfitLossPageResponse>;
  getRefundsGridData: (body: RefundsGridDataRequest) => Promise<RefundDataGridResponse>;
  getRefundsCardsData: (body: RefundsCardsDataRequest) => Promise<RefundCardsDataResponse>;
  getTotalTaxesData: (body: TaxesRequestDto) => Promise<TotalTaxesResponse>;
  getTaxesGridData: (body: TaxesRequestDto) => Promise<TaxesGridResponse>;
  getTaxesByTypeData: (body: TaxesRequestDto) => Promise<TaxesByTypeResponse>;
  getDiscountReportKPICardsData: (body: DiscountReportKPICardsRequest) => Promise<DiscountReportKPICardsResponse>;
  getDiscountReportTop5DiscountsData: (
    body: DiscountReportCommonRequest
  ) => Promise<DiscountReportTop5DiscountsResponse>;
  getDiscountReportGridData: (body: DiscountReportCommonRequest) => Promise<DiscountReportGridDataResponse>;
  getSalesByRevenueCenterData: (body: RevenueCenterCommonRequest) => Promise<SalesByRevenueCenterResponse>;
  getPerPersonAverageByRevenueCenterData: (
    body: RevenueCenterCommonRequest
  ) => Promise<PerPersonAverageByRevenueCenterResponse>;
  getRevenueCenterGridData: (body: RevenueCenterCommonRequest) => Promise<RevenueCenterGridResponse>;
  getVoidsReportGridData: (body: VoidsReportCommonRequest) => Promise<VoidsReportGridDataResponse>;
  getVoidsReportTop5ReasonsData: (body: VoidsReportCommonRequest) => Promise<VoidsReportTop5ReasonsDataResponse>;
  getVoidsReportKPICardsData: (body: VoidsReportKPICardsDataRequest) => Promise<VoidsReportKPICardsDataResponse>;
  getTotalPaymentsData: (body: PaymentsComparisonRequestDto) => Promise<TotalPaymentsResponseDto>;
  getPaymentsByTypeData: (body: PaymentsRequestDto) => Promise<PaymentsByTypeResponseDto>;
  getPaymentsGridData: (body: PaymentsRequestDto) => Promise<PaymentsGridResponseDto>;
  exportPaymentsGridData: (body: PaymentsRequestDto) => Promise<ExportFileReportGridResponse | void>;
  getDaypartFiltersBslRequest: (siteIds: string[]) => Promise<FilterValuesResponse>;
  getTotalSalesByShift: (date: string) => Promise<TotalSalesByShiftResponseDto[]>;
};

const useSalesRequests = (): SalesRequests => {
  const { t } = useTranslation();
  const { setSnackbarState } = useSnackbar();
  const { siteId } = useCurrentSite();
  const callBsl = useCallBsl();
  const user = useUsers();

  const throwBspError = useCallback(
    (err: unknown) => {
      // eslint-disable-next-line no-console
      if (process.env.NODE_ENV === 'development') console.error(err);

      setSnackbarState({
        open: true,
        message: t('sales.errors.genericMessage'),
        color: 'error',
      });
      return err;
    },
    [t, setSnackbarState]
  );

  const convertPdfResponseToBlobUrl = useCallback((res: PdfResponse): string => {
    const buffer = Buffer.from(res.data);
    const blob = new Blob([buffer], { type: 'application/pdf' });
    return URL.createObjectURL(blob);
  }, []);

  const getFilterValues = useCallback(
    async (body: FilterRequestBody) =>
      callBsl<FilterValuesResponse>({
        proxy: proxies.sales,
        verb: Verb.POST,
        pathSegments: ['filters'],
        payload: body,
      }).catch((err) => {
        throwBspError(err);
        return {
          results: [],
        };
      }),
    [throwBspError, callBsl]
  );

  const getDaypartFiltersBslRequest = useCallback(
    async (siteIds: string[]) =>
      callBsl<FilterValuesResponse>({
        proxy: proxies.siteManagement,
        verb: Verb.POST,
        pathSegments: ['sites', 'daypart-filters'],
        payload: siteIds,
      }).catch((err) => {
        throwBspError(err);
        return {
          results: [],
        };
      }),
    [throwBspError, callBsl]
  );

  const getHouseAccountsSummary = useCallback(
    async (): Promise<HouseAccountSummary[]> =>
      callBsl<HouseAccountSummaryPage>({
        proxy: proxies.houseAccounts,
        verb: Verb.GET,
        pathSegments: ['house-accounts'],
      })
        .then((res: HouseAccountSummaryPage): HouseAccountSummary[] => res.result)
        .catch((err) => {
          throwBspError(err);
          return [];
        }),
    [throwBspError, callBsl]
  );

  const createHouseAccount = useCallback(
    async (body: HouseAccountRequestSchema): Promise<HouseAccountResponseSchema | undefined> =>
      callBsl<HouseAccountResponseSchema | undefined>({
        proxy: proxies.houseAccounts,
        verb: Verb.POST,
        pathSegments: ['house-accounts'],
        enterpriseUnit: siteId,
        payload: body,
      }).catch((err) => {
        const error = err as IApiError;
        if (error.details?.message?.includes('houseAccountName must be unique')) {
          throw err;
        }
        throwBspError(err);
        return undefined;
      }),
    [siteId, throwBspError, callBsl]
  );

  const getHouseAccountsActivities = useCallback(
    async (houseAccountId: string): Promise<HouseAccountActivity[]> =>
      callBsl<HouseAccountActivitiesPage>({
        proxy: proxies.houseAccounts,
        verb: Verb.GET,
        pathSegments: ['house-accounts', houseAccountId, 'activities'],
      })
        .then((res: HouseAccountActivitiesPage): HouseAccountActivity[] =>
          res.result.map((activity) => {
            const consumerInfo = activity.consumerInfo ? JSON.parse(activity.consumerInfo) : undefined;
            return { ...activity, consumerInfo };
          })
        )
        .catch((err) => {
          throwBspError(err);
          return [];
        }),
    [throwBspError, callBsl]
  );

  const getHouseAccount = useCallback(
    async (houseAccountId: string): Promise<HouseAccount | undefined> =>
      callBsl<HouseAccount>({
        proxy: proxies.houseAccounts,
        verb: Verb.GET,
        pathSegments: ['house-accounts', houseAccountId],
      }).catch((err) => {
        throwBspError(err);
        return undefined;
      }),
    [throwBspError, callBsl]
  );

  const putHouseAccount = useCallback(
    async (houseAccountId: string, body: HouseAccountRequestSchema): Promise<HouseAccount | undefined> =>
      callBsl<HouseAccount>({
        proxy: proxies.houseAccounts,
        verb: Verb.PUT,
        pathSegments: ['house-accounts', houseAccountId],
        enterpriseUnit: siteId,
        payload: body,
      }).catch((err) => {
        const error = err as IApiError;
        if (error?.details?.message?.includes('houseAccountName must be unique')) {
          throw err;
        }
        throwBspError(err);
        return undefined;
      }),
    [siteId, throwBspError, callBsl]
  );

  const createStatement = useCallback(
    async (houseAccountId: string, body: StatementBodySchema): Promise<HouseAccountStatement | undefined> =>
      callBsl<HouseAccountStatement>({
        proxy: proxies.houseAccounts,
        verb: Verb.POST,
        pathSegments: ['house-accounts', houseAccountId, 'statements'],
        enterpriseUnit: siteId,
        payload: body,
      }).catch((err) => {
        throwBspError(err);
        return undefined;
      }),
    [siteId, throwBspError, callBsl]
  );

  const deleteStatement = useCallback(
    async (statementId: string): Promise<boolean> =>
      callBsl<HouseAccountStatement>({
        proxy: proxies.houseAccounts,
        verb: Verb.DELETE,
        pathSegments: ['house-accounts', 'statements', statementId],
        enterpriseUnit: siteId,
      })
        .then(() => true)
        .catch((err) => {
          throwBspError(err);
          return false;
        }),
    [siteId, throwBspError, callBsl]
  );

  const patchStatement = useCallback(
    async (statementId: string, body: PatchStatementBodySchema) =>
      callBsl<HouseAccountStatement>({
        proxy: proxies.houseAccounts,
        verb: Verb.PATCH,
        pathSegments: ['house-accounts', 'statements', statementId],
        enterpriseUnit: siteId,
        payload: body,
      })
        .then(() => true)
        .catch((err) => {
          throwBspError(err);
          return false;
        }),
    [siteId, throwBspError, callBsl]
  );

  const getStatement = useCallback(
    async (statementId: string): Promise<HouseAccountStatement> =>
      callBsl<HouseAccountStatement>({
        proxy: proxies.houseAccounts,
        verb: Verb.GET,
        pathSegments: ['house-accounts', 'statements', statementId],
      }),
    [callBsl]
  );

  const createStatementPDF = useCallback(
    async (body: CreateStatementPDFBodySchema) =>
      callBsl<PdfResponse>({
        proxy: proxies.houseAccounts,
        verb: Verb.POST,
        pathSegments: ['house-accounts', 'statements', 'pdf'],
        payload: body,
      }).then(convertPdfResponseToBlobUrl),
    [callBsl, convertPdfResponseToBlobUrl]
  );

  const patchHouseAccount = useCallback(
    async (houseAccountId: string, body: UpdateHouseAccountRequestSchema): Promise<boolean> =>
      callBsl<HouseAccountStatement>({
        proxy: proxies.houseAccounts,
        verb: Verb.PATCH,
        pathSegments: ['house-accounts', houseAccountId],
        enterpriseUnit: siteId,
        payload: body,
      })
        .then(() => true)
        .catch((err) => {
          throwBspError(err);
          return false;
        }),
    [siteId, throwBspError, callBsl]
  );

  const deleteHouseAccount = useCallback(
    async (houseAccountId: string): Promise<boolean> =>
      callBsl<HouseAccountStatement>({
        proxy: proxies.houseAccounts,
        verb: Verb.DELETE,
        pathSegments: ['house-accounts', houseAccountId],
        enterpriseUnit: siteId,
      })
        .then(() => true)
        .catch((err) => {
          throwBspError(err);
          return false;
        }),
    [siteId, throwBspError, callBsl]
  );

  const restoreHouseAccount = useCallback(
    async (houseAccountId: string): Promise<boolean> =>
      callBsl<HouseAccountStatement>({
        proxy: proxies.houseAccounts,
        verb: Verb.POST,
        pathSegments: ['house-accounts', houseAccountId, 'restore'],
        enterpriseUnit: siteId,
      })
        .then(() => true)
        .catch((err) => {
          throwBspError(err);
          return false;
        }),
    [siteId, throwBspError, callBsl]
  );

  const createTransaction = useCallback(
    async (houseAccountId: string, body: CreateTransactionBodySchema): Promise<Transaction> =>
      callBsl<Transaction>({
        proxy: proxies.houseAccounts,
        verb: Verb.POST,
        pathSegments: ['house-accounts', houseAccountId, 'transactions'],
        enterpriseUnit: siteId,
        payload: body,
      }),
    [siteId, callBsl]
  );

  const getTransaction = useCallback(
    async (transactionId: string): Promise<Transaction | undefined> =>
      callBsl<Transaction>({
        proxy: proxies.houseAccounts,
        verb: Verb.GET,
        pathSegments: ['house-accounts', 'transactions', transactionId],
      }).catch((err) => {
        throwBspError(err);
        return undefined;
      }),
    [throwBspError, callBsl]
  );

  const getGuestTransaction = useCallback(
    async (
      transactionId: string,
      businessDate: string
    ): Promise<SalesResponses.GetGuestTransactionDetailsResponseDto | undefined> =>
      callBsl<SalesResponses.GetGuestTransactionDetailsResponseDto>({
        proxy: proxies.sales,
        verb: Verb.GET,
        pathSegments: ['guest-checks', businessDate, transactionId],
      }).catch((err) => {
        throwBspError(err);
        return undefined;
      }),
    [throwBspError, callBsl]
  );

  const getSaleTransaction = useCallback(
    async (transactionId: string): Promise<SaleTransaction | undefined> =>
      callBsl<SaleTransaction>({
        proxy: proxies.houseAccounts,
        verb: Verb.GET,
        pathSegments: ['house-accounts', 'transactions', transactionId],
      }).catch((err) => {
        throwBspError(err);
        return undefined;
      }),
    [throwBspError, callBsl]
  );

  const createPdf = useCallback(
    async (body: CreateTransactionPDFBodySchema | SalesRequestTypes.TransactionPdfDto) =>
      callBsl<PdfResponse>({
        proxy: proxies.sales,
        verb: Verb.POST,
        pathSegments: ['transactions', 'pdf'],
        payload: body,
      })
        .then(convertPdfResponseToBlobUrl)
        .catch((err) => {
          throwBspError(err);
          return undefined;
        }),
    [throwBspError, callBsl, convertPdfResponseToBlobUrl]
  );

  const handleOverflowError = useCallback(
    (err: BodyOverflowError) => {
      if (SalesUtilities.isBodyOverflowError(err)) {
        throw err;
      }

      throwBspError(err);
      return [];
    },
    [throwBspError]
  );

  const getAllGuestChecks = useCallback(
    async (body: GuestCheckRequest) =>
      callBsl<GuestCheckResponse[]>({
        proxy: proxies.sales,
        verb: Verb.GET,
        pathSegments: ['guest-checks'],
        queryParams: [
          { kind: 'multi', key: 'siteIds', value: body.siteIds },
          { kind: 'single', key: 'startDate', value: body.startDate },
          { kind: 'single', key: 'endDate', value: body.endDate },
          { kind: 'single', key: 'hasComps', value: body.hasComps },
          { kind: 'single', key: 'hasPromos', value: body.hasPromos },
          { kind: 'single', key: 'hasRefunds', value: body.hasRefunds },
          { kind: 'single', key: 'hasVoids', value: body.hasVoids },
        ],
      }).catch(handleOverflowError),
    [handleOverflowError, callBsl]
  );

  const exportAllGuestChecks = async (body: GuestCheckRequest): Promise<ExportFileReportGridResponse | void> => {
    const data: ExportFileReportGridResponse = await callBsl({
      proxy: proxies.sales,
      verb: Verb.POST,
      pathSegments: ['guest-checks', 'export'],
      payload: body,
    });
    if (data.signedUrl) {
      downloadFile(data.signedUrl);
    } else {
      throw new Error('signedUrl not found');
    }
    return data;
  };

  const getPOSEventFilters = useCallback(
    async (body: POSEventFiltersRequest) =>
      callBsl<POSEventFiltersResponse>({
        proxy: proxies.sales,
        verb: Verb.POST,
        pathSegments: ['filters', 'pos-event-log'],
        payload: body,
      }).catch((err) => {
        throwBspError(err);
        return {
          terminalId: [],
          employee: [],
        } as POSEventFiltersResponse;
      }),
    [throwBspError, callBsl]
  );

  const getPOSEvents = useCallback(
    async (body: POSEventsRequest) =>
      callBsl<POSEventsResponse[]>({
        proxy: proxies.sales,
        verb: Verb.POST,
        pathSegments: ['pos-events'],
        payload: body,
      }).catch(handleOverflowError),
    [handleOverflowError, callBsl]
  );

  const getKPIData = useCallback(
    async (body: KPIDataRequest) =>
      callBsl<KPIDataResponse>({
        proxy: proxies.sales,
        verb: Verb.POST,
        pathSegments: ['dashboard', 'kpi'],
        payload: body,
        enterpriseUnit: siteId,
      }).catch((err) => {
        throwBspError(err);
        return {
          isSuccess: false,
          reportMetricResults: {},
        };
      }),
    [siteId, throwBspError, callBsl]
  );

  const getSalesSummaryCardData = useCallback(
    async (body: SalesSummaryDataRequest) =>
      callBsl<SalesSummaryDataResponse>({
        proxy: proxies.sales,
        verb: Verb.POST,
        pathSegments: ['sales-summary', 'sales-cards'],
        payload: body,
      }).catch((err) => {
        throwBspError(err);
        return {
          netSales: 0,
          discounts: 0,
          charges: 0,
          taxes: 0,
          grossSales: 0,
        };
      }),
    [throwBspError, callBsl]
  );

  const exportSalesSummaryGridData = async (
    body: SalesSummaryDataRequest
  ): Promise<ExportFileReportGridResponse | void> => {
    const data: ExportFileReportGridResponse = await callBsl({
      proxy: proxies.sales,
      verb: Verb.POST,
      pathSegments: ['sales-summary', 'sales-grid', 'export'],
      payload: body,
    });
    if (data.signedUrl) {
      downloadFile(data.signedUrl);
    } else {
      throw new Error('signedUrl not found');
    }
    return data;
  };

  const getComparativeSalesLaborGridData = useCallback(
    async (body: BaseReportComparisonDataRequest) =>
      callBsl<ComparativeSalesDataGridResults[]>({
        proxy: proxies.sales,
        verb: Verb.POST,
        pathSegments: ['comparative-sales-labor', 'grid-data'],
        payload: body,
      }).catch((err) => {
        throwBspError(err);
        return [];
      }),
    [throwBspError, callBsl]
  );

  const getComparativeSalesLaborCardsData = useCallback(
    async (body: BaseReportComparisonDataRequest) =>
      callBsl<ComparativeSalesCardResults>({
        proxy: proxies.sales,
        verb: Verb.POST,
        pathSegments: ['comparative-sales-labor', 'kpi-cards'],
        payload: body,
      }).catch((err) => {
        throwBspError(err);
        return {
          totalNetSales: 0,
          totalLaborCost: 0,
          totalNetSalesVariance: 0,
          totalLaborCostVariance: 0,
          top5NetSales: [],
          bottom5NetSales: [],
          top5SalesPerLaborHour: [],
          bottom5SalesPerLaborHour: [],
        };
      }),
    [throwBspError, callBsl]
  );

  const exportComparativeSalesLaborPdf = useCallback(
    async (body: ComparativeSalesLaborPdfExportRequest) =>
      callBsl<PdfResponse>({
        proxy: proxies.sales,
        verb: Verb.POST,
        pathSegments: ['comparative-sales-labor', 'export-pdf'],
        payload: body,
      })
        .then(convertPdfResponseToBlobUrl)
        .catch((err) => {
          throwBspError(err);
          return undefined;
        }),
    [throwBspError, callBsl, convertPdfResponseToBlobUrl]
  );

  const exportComparativeSalesLaborCsv = async (
    body: ComparativeSalesLaborCsvExportRequest
  ): Promise<ExportFileReportGridResponse | void> => {
    const data: ExportFileReportGridResponse = await callBsl({
      proxy: proxies.sales,
      verb: Verb.POST,
      pathSegments: ['comparative-sales-labor', 'grid-data', 'export'],
      payload: body,
    });
    if (data.signedUrl) {
      downloadFile(data.signedUrl);
    } else {
      throw new Error('signedUrl not found');
    }
    return data;
  };

  const getSalesSummaryGridData = useCallback(
    async (body: SalesSummaryDataRequest) =>
      callBsl<SalesSummaryGridResponseDto>({
        proxy: proxies.sales,
        verb: Verb.POST,
        pathSegments: ['sales-summary', 'sales-grid'],
        payload: body,
      }).catch((err) => {
        throwBspError(err);
        return {
          results: [],
        };
      }),
    [throwBspError, callBsl]
  );

  const getSalesSummaryPaymentsGridData = useCallback(
    async (body: SalesSummaryDataRequest) =>
      callBsl<SalesPaymentsGridResponseDto>({
        proxy: proxies.sales,
        verb: Verb.POST,
        pathSegments: ['sales-summary', 'payments-grid'],
        payload: body,
      }).catch((err) => {
        throwBspError(err);
        return {
          results: [],
        };
      }),
    [throwBspError, callBsl]
  );

  const exportSalesSummaryPaymentsGridData = async (
    body: SalesSummaryDataRequest
  ): Promise<ExportFileReportGridResponse | void> => {
    const data: ExportFileReportGridResponse = await callBsl({
      proxy: proxies.sales,
      verb: Verb.POST,
      pathSegments: ['sales-summary', 'payments-grid', 'export'],
      payload: body,
    });
    if (data.signedUrl) {
      downloadFile(data.signedUrl);
    } else {
      throw new Error('signedUrl not found');
    }
    return data;
  };

  const getRefundsGridData = useCallback(
    async (body: RefundsGridDataRequest) =>
      callBsl<RefundDataGridResponse>({
        proxy: proxies.sales,
        verb: Verb.POST,
        pathSegments: ['refunds', 'grid-data'],
        payload: body,
      }).catch((err) => {
        throwBspError(err);
        return {
          results: [],
        };
      }),
    [throwBspError, callBsl]
  );

  const getRefundsCardsData = useCallback(
    async (body: RefundsCardsDataRequest) =>
      callBsl<RefundCardsDataResponse>({
        proxy: proxies.sales,
        verb: Verb.POST,
        pathSegments: ['refunds'],
        payload: body,
      }).catch((err) => {
        throwBspError(err);
        return {
          refundedCount: 0,
          refundAmount: 0,
          refundCountComparisonPercent: 0,
          refundAmountComparisonPercent: 0,
        };
      }),
    [throwBspError, callBsl]
  );

  const getKPIData2 = useCallback(
    async (body: KPIDataRequest) =>
      callBsl<KPIDataResponse>({
        proxy: proxies.sales,
        verb: Verb.GET,
        pathSegments: ['dashboard', 'kpi'],
        enterpriseUnit: siteId,
        queryParams: [
          { kind: 'single', key: 'startDate', value: body.startDate },
          { kind: 'single', key: 'endDate', value: body.endDate },
          { kind: 'multi', key: 'metricsRequested', value: body.metricsRequested },
        ],
      }).catch((err) => {
        throwBspError(err);
        return {
          isSuccess: false,
          reportMetricResults: {},
        } as KPIDataResponse;
      }),
    [siteId, throwBspError, callBsl]
  );

  const getDashboardGraphData = useCallback(
    async (body: DashboardGraphDataRequest) =>
      callBsl<DashboardGraphDataResponse>({
        proxy: proxies.sales,
        verb: Verb.GET,
        pathSegments: ['dashboard', 'graph-data'],
        enterpriseUnit: siteId,
        queryParams: [
          { kind: 'single', key: 'startDate', value: body.startDate },
          { kind: 'single', key: 'endDate', value: body.endDate },
        ],
      }).catch((err) => {
        throwBspError(err);
        return {
          xAxis: [],
          netSales: [],
          comparisonNetSales: [],
          forecastedNetSales: [],
        } as DashboardGraphDataResponse;
      }),
    [siteId, throwBspError, callBsl]
  );

  const getProductMixMenuGridData = useCallback(
    async (body: ProductMixMenuGridDataRequest) =>
      callBsl<ProductMixMenuGridDataResponse>({
        proxy: proxies.sales,
        verb: Verb.POST,
        pathSegments: ['productmix', 'grid-data'],
        payload: body,
      }).catch((err) => {
        throwBspError(err);
        return {
          results: [],
        };
      }),
    [throwBspError, callBsl]
  );

  const exportProductMixMenuGridData = async (
    body: ProductMixMenuGridDataExportRequest
  ): Promise<ExportFileReportGridResponse | void> => {
    const data: ExportFileReportGridResponse = await callBsl({
      proxy: proxies.sales,
      verb: Verb.POST,
      pathSegments: ['productmix', 'grid-data', 'export'],
      payload: body,
    });
    if (data.signedUrl) {
      downloadFile(data.signedUrl);
    } else {
      throw new Error('signedUrl not found');
    }
    return data;
  };

  const getProductMixTop5Items = useCallback(
    async (body: ProductMixTop5ItemsRequest) =>
      callBsl<ProductMixTop5ItemsResponse>({
        proxy: proxies.sales,
        verb: Verb.POST,
        pathSegments: ['productmix', 'top-items'],
        payload: body,
      }).catch((err) => {
        throwBspError(err);
        return {
          items: [],
        };
      }),
    [throwBspError, callBsl]
  );

  const getProductMixTotalSales = useCallback(
    async (body: ProductMixTotalSalesRequest) =>
      callBsl<ProductMixTotalSalesResponse>({
        proxy: proxies.sales,
        verb: Verb.POST,
        pathSegments: ['productmix', 'total-sales'],
        payload: body,
      }).catch((err) => {
        throwBspError(err);
        return {
          selectedDateRange: [],
          comparisonDateRange: [],
        };
      }),
    [throwBspError, callBsl]
  );

  const getProductMixTop5Sellers = useCallback(
    async (body: ProductMixEmployeeSalesDataRequest) =>
      callBsl<ProductMixTop5SellersResponse>({
        proxy: proxies.sales,
        verb: Verb.POST,
        pathSegments: ['productmix', 'top-sellers'],
        payload: body,
      }).catch((err) => {
        throwBspError(err);
        return {
          topSellers: [],
        };
      }),
    [throwBspError, callBsl]
  );

  const getProductMixEmployeeSalesGrid = useCallback(
    async (body: ProductMixEmployeeSalesDataRequest) =>
      callBsl<ProductMixEmployeeSalesGridResponse>({
        proxy: proxies.sales,
        verb: Verb.POST,
        pathSegments: ['productmix', 'employee-sales-grid-data'],
        payload: body,
      }).catch((err) => {
        throwBspError(err);
        return {
          results: [],
        };
      }),
    [throwBspError, callBsl]
  );

  const exportProductMixEmployeeSalesGridData = async (
    body: ProductMixEmployeeSalesDataRequest
  ): Promise<ExportFileReportGridResponse | void> => {
    const data: ExportFileReportGridResponse = await callBsl({
      proxy: proxies.sales,
      verb: Verb.POST,
      pathSegments: ['productmix', 'employee-sales-grid-data', 'export'],
      payload: body,
    });
    if (data.signedUrl) {
      downloadFile(data.signedUrl);
    } else {
      throw new Error('signedUrl not found');
    }
    return data;
  };

  const getCalendarFilters = useCallback(
    async (): Promise<Calendar.FiscalCalendarFilters | undefined> =>
      callBsl<Calendar.FiscalCalendarFilters>({
        proxy: proxies.calendar,
        verb: Verb.GET,
        pathSegments: ['fiscal-calendar', 'filters'],
      }).catch((err) => {
        throwBspError(err);
        return undefined;
      }),
    [throwBspError, callBsl]
  );

  const getGlAccounts = useCallback(
    async (body: GlAccountsRequest) =>
      callBsl<GlAccountsResponse>({
        proxy: proxies.glAccounts,
        verb: Verb.GET,
        pathSegments: ['gl-accounts'],
        queryParams:
          // If all the arrays are empty it causes a weird authentication error, so avoid that.
          body.isActive ||
          body.categoryIds.length > 0 ||
          body.accountTypes.length > 0 ||
          body.accountDetailTypes.length > 0
            ? SalesUtilities.toGetGlAccountsQueryParams(body)
            : undefined,
      }).catch((err) => {
        throwBspError(err);
        return undefined;
      }),
    [throwBspError, callBsl]
  );

  const getGlAccountFilters = useCallback(
    async () =>
      callBsl<GlAccountFiltersResponse>({
        proxy: proxies.glAccounts,
        verb: Verb.GET,
        pathSegments: ['gl-accounts', 'filters'],
      }).catch((err) => {
        throwBspError(err);
        return undefined;
      }),
    [throwBspError, callBsl]
  );

  const getGlAccountUpsertInfo = useCallback(
    async () =>
      callBsl<GlAccountUpsertInfoResponse>({
        proxy: proxies.glAccounts,
        verb: Verb.GET,
        pathSegments: ['gl-accounts', 'info'],
      }).catch((err) => {
        throwBspError(err);
        return err;
      }),
    [throwBspError, callBsl]
  );

  const getAllGlAccountNames = useCallback(
    async (categoryIds?: number[]) =>
      callBsl<GlAccountName[]>({
        proxy: proxies.glAccounts,
        verb: Verb.GET,
        pathSegments: ['gl-accounts', 'names'],
        queryParams: categoryIds
          ? [{ kind: 'multi', key: 'categoryIds', value: categoryIds.map((id) => id.toString()) }]
          : undefined,
      }).catch((err) => {
        throwBspError(err);
        return err;
      }),
    [throwBspError, callBsl]
  );

  const deleteGlAccount = useCallback(
    async (body: GlAccountsDeleteRequest): Promise<boolean> =>
      callBsl<GlAccountsResponse>({
        proxy: proxies.glAccounts,
        verb: Verb.DELETE,
        pathSegments: ['gl-accounts'],
        payload: body,
      })
        .then(() => true)
        .catch((err) => {
          throwBspError(err);
          return false;
        }),
    [throwBspError, callBsl]
  );

  const createGlAccount = useCallback(
    async (body: CreateGlAccountRequest): Promise<CreateGlAccountsResponse> =>
      callBsl<CreateGlAccountsResponse>({
        proxy: proxies.glAccounts,
        verb: Verb.POST,
        pathSegments: ['gl-accounts'],
        payload: body,
      }).catch((err) => err.details?.constraintViolations ?? t('sales.errors.genericMessage')),
    [callBsl, t]
  );

  const updateGlAccount = useCallback(
    async (glAccountId: string, body: UpdateGlAccountRequest): Promise<boolean> =>
      callBsl<void>({
        proxy: proxies.glAccounts,
        verb: Verb.PUT,
        pathSegments: ['gl-accounts', glAccountId],
        payload: body,
      })
        .then(() => true)
        .catch((err) => err.details?.constraintViolations ?? t('sales.errors.genericMessage')),
    [t, callBsl]
  );

  const restoreDeleteGlAccount = useCallback(
    async (body: GlAccountsDeleteRequest): Promise<boolean> =>
      callBsl<GlAccountsResponse>({
        proxy: proxies.glAccounts,
        verb: Verb.PATCH,
        pathSegments: ['gl-accounts'],
        payload: body,
      })
        .then(() => true)
        .catch((err) => {
          throwBspError(err);
          return false;
        }),
    [throwBspError, callBsl]
  );

  const updateIsActiveGlAccounts = useCallback(
    async (body: UpdateIsActiveGlAccountsRequest): Promise<boolean> =>
      callBsl<void>({
        proxy: proxies.glAccounts,
        verb: Verb.PUT,
        pathSegments: ['gl-accounts'],
        payload: body,
      })
        .then(() => true)
        .catch((err) => {
          throwBspError(err);
          return false;
        }),
    [throwBspError, callBsl]
  );

  const uploadGlAccounts = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async (file: File): Promise<AxiosResponse<any, any>> => {
      if (!user.oktaToken) {
        throw new Error('No okta token found');
      }

      // Add all required values for making bulk-import endpoint call inside form-data
      const formData = new FormData();
      formData.append('file', file);
      formData.append('bslId', `${user.org?.bslId}`);
      formData.append('proxy', proxies.glAccounts);
      formData.append('verb', Verb.PUT);
      formData.append('pathSegments', 'gl-accounts,bulk-import');
      formData.append('oktaJwt', user.oktaToken);

      // Make proxy url string
      const path = '/bslproxyncridwithformdata';
      const proxyUrl = `${process.env.REACT_APP_FIREBASE_FUNCTION_HOST}${path}`;

      // Make headers for request
      const config = {
        headers: {
          Authorization: `Bearer ${user.oktaToken}`,
        },
      };

      return axios.post(proxyUrl, formData, config);
    },
    [user.oktaToken, user.org?.bslId]
  );

  const getGlAccountById = useCallback(
    async (glAccountId: string) =>
      callBsl<GlAccount>({
        verb: Verb.GET,
        proxy: proxies.glAccounts,
        pathSegments: ['gl-accounts', 'accountId', glAccountId],
      }).catch((err) => {
        setSnackbarState({
          open: true,
          message: t('sales.errors.genericMessage'),
          color: 'error',
        });
        return undefined;
      }),
    [callBsl, setSnackbarState, t]
  );

  const getSalesDefinitionsSettings = useCallback(
    async () =>
      callBsl<SalesDefinitionsSettings>({
        proxy: proxies.sales,
        verb: Verb.GET,
        pathSegments: ['sales-report-config'],
      }).catch((err) => {
        throwBspError(err);
        return err;
      }),
    [throwBspError, callBsl]
  );

  const updateSalesDefinitionsSettings = useCallback(
    async (body: UpdateSalesDefinitionsSettings): Promise<UpdateSalesDefinitionsResponse> =>
      callBsl<void>({
        proxy: proxies.sales,
        verb: Verb.PUT,
        pathSegments: ['sales-report-config'],
        payload: body,
      }).catch((err) => {
        throwBspError(err);
        return err;
      }),
    [throwBspError, callBsl]
  );

  const getProfitLossGridData = useCallback(
    async (body: ProfitLossGridDataRequest) =>
      callBsl<ProfitLossPageResponse>({
        proxy: proxies.sales,
        verb: Verb.POST,
        pathSegments: ['profit-loss', 'grid-data'],
        payload: body,
      }).catch((err) => {
        throwBspError(err);
        return {} as ProfitLossPageResponse;
      }),
    [throwBspError, callBsl]
  );

  const getTotalTaxesData = useCallback(
    async (body: TaxesRequestDto) =>
      callBsl<TotalTaxesResponse>({
        proxy: proxies.sales,
        verb: Verb.POST,
        pathSegments: ['taxes', 'total-taxes'],
        payload: body,
      }).catch((err) => {
        throwBspError(err);
        return err;
      }),
    [throwBspError, callBsl]
  );

  const getTaxesGridData = useCallback(
    async (body: TaxesRequestDto) =>
      callBsl<TaxesGridResponse>({
        proxy: proxies.sales,
        verb: Verb.POST,
        pathSegments: ['taxes', 'grid-data'],
        payload: body,
      }).catch((err) => {
        throwBspError(err);
        return {
          results: [],
        };
      }),
    [throwBspError, callBsl]
  );

  const getTaxesByTypeData = useCallback(
    async (body: TaxesRequestDto) =>
      callBsl<TaxesByTypeResponse>({
        proxy: proxies.sales,
        verb: Verb.POST,
        pathSegments: ['taxes', 'taxes-by-type'],
        payload: body,
      }).catch((err) => {
        throwBspError(err);
        return {
          results: [],
        };
      }),
    [throwBspError, callBsl]
  );

  const getDiscountReportKPICardsData = useCallback(
    async (body: DiscountReportKPICardsRequest) =>
      callBsl<DiscountReportKPICardsResponse>({
        proxy: proxies.sales,
        verb: Verb.POST,
        pathSegments: ['discounts', 'kpi-cards'],
        payload: body,
      }).catch((err) => {
        throwBspError(err);
        return {
          countOfDiscounts: 0,
          discountCountPercentChange: 0,
          totalAmountOfDiscounts: 0,
          discountAmountPercentChange: 0,
        };
      }),
    [throwBspError, callBsl]
  );

  const getDiscountReportTop5DiscountsData = useCallback(
    async (body: DiscountReportCommonRequest) =>
      callBsl<DiscountReportTop5DiscountsResponse>({
        proxy: proxies.sales,
        verb: Verb.POST,
        pathSegments: ['discounts', 'top-discounts'],
        payload: body,
      }).catch((err) => {
        throwBspError(err);
        return {
          discountItems: [],
        };
      }),
    [throwBspError, callBsl]
  );

  const getDiscountReportGridData = useCallback(
    async (body: DiscountReportCommonRequest) =>
      callBsl<DiscountReportGridDataResponse>({
        proxy: proxies.sales,
        verb: Verb.POST,
        pathSegments: ['discounts', 'grid-data'],
        payload: body,
      }).catch((err) => {
        throwBspError(err);
        return {
          results: [],
        };
      }),
    [throwBspError, callBsl]
  );

  const getSalesByRevenueCenterData = useCallback(
    async (body: RevenueCenterCommonRequest) =>
      callBsl<SalesByRevenueCenterResponse>({
        proxy: proxies.sales,
        verb: Verb.POST,
        pathSegments: ['revenue-centers', 'sales-by-revenue-center'],
        payload: body,
      }).catch((err) => {
        throwBspError(err);
        return {
          results: [],
        };
      }),
    [throwBspError, callBsl]
  );

  const getPerPersonAverageByRevenueCenterData = useCallback(
    async (body: RevenueCenterCommonRequest) =>
      callBsl<PerPersonAverageByRevenueCenterResponse>({
        proxy: proxies.sales,
        verb: Verb.POST,
        pathSegments: ['revenue-centers', 'per-person-average'],
        payload: body,
      }).catch((err) => {
        throwBspError(err);
        return {
          results: [],
        };
      }),
    [throwBspError, callBsl]
  );

  const getRevenueCenterGridData = useCallback(
    async (body: RevenueCenterCommonRequest) =>
      callBsl<RevenueCenterGridResponse>({
        proxy: proxies.sales,
        verb: Verb.POST,
        pathSegments: ['revenue-centers', 'grid'],
        payload: body,
      }).catch((err) => {
        throwBspError(err);
        return {
          results: [],
        };
      }),
    [throwBspError, callBsl]
  );

  const getVoidsReportGridData = useCallback(
    async (body: VoidsReportCommonRequest) =>
      callBsl<VoidsReportGridDataResponse>({
        proxy: proxies.sales,
        verb: Verb.POST,
        pathSegments: ['voids', 'grid-data'],
        payload: body,
      }).catch((err) => {
        throwBspError(err);
        return {
          results: [],
        };
      }),
    [throwBspError, callBsl]
  );

  const getVoidsReportTop5ReasonsData = useCallback(
    async (body: VoidsReportCommonRequest) =>
      callBsl<VoidsReportTop5ReasonsDataResponse>({
        proxy: proxies.sales,
        verb: Verb.POST,
        pathSegments: ['voids', 'top-void-reasons'],
        payload: body,
      }).catch((err) => {
        throwBspError(err);
        return {
          voidReasons: [],
        };
      }),
    [throwBspError, callBsl]
  );

  const getVoidsReportKPICardsData = useCallback(
    async (body: VoidsReportKPICardsDataRequest) =>
      callBsl<VoidsReportKPICardsDataResponse>({
        proxy: proxies.sales,
        verb: Verb.POST,
        pathSegments: ['voids', 'kpi-cards'],
        payload: body,
      }).catch((err) => {
        throwBspError(err);
        return {
          totalVoidsCount: 0,
          voidsCountPercentChange: 0,
          totalVoidsAmount: 0,
          voidsAmountPercentChange: 0,
        };
      }),
    [throwBspError, callBsl]
  );

  const getTotalPaymentsData = useCallback(
    async (body: PaymentsComparisonRequestDto) =>
      callBsl<TotalPaymentsResponseDto>({
        proxy: proxies.sales,
        verb: Verb.POST,
        pathSegments: ['payments', 'total-payments'],
        payload: body,
      }).catch((err) => {
        throwBspError(err);
        return {
          paymentsAmount: 0,
          comparisonPaymentsAmount: 0,
          amountComparisonPercentage: 0,
        };
      }),
    [throwBspError, callBsl]
  );

  const getPaymentsByTypeData = useCallback(
    async (body: PaymentsRequestDto) =>
      callBsl<PaymentsByTypeResponseDto>({
        proxy: proxies.sales,
        verb: Verb.POST,
        pathSegments: ['payments', 'payments-by-type'],
        payload: body,
      }).catch((err) => {
        throwBspError(err);
        return {
          results: [],
        };
      }),
    [throwBspError, callBsl]
  );

  const getTotalSalesByShift = useCallback(
    async (date: string) =>
      callBsl<TotalSalesByShiftResponseDto[]>({
        proxy: proxies.sales,
        verb: Verb.GET,
        pathSegments: ['punch-summary', 'total-sales', siteId ?? '', date],
      }).catch((err) => {
        throwBspError(err);
        return [];
      }),
    [throwBspError, callBsl, siteId]
  );

  const getPaymentsGridData = useCallback(
    async (body: PaymentsRequestDto) =>
      callBsl<PaymentsGridResponseDto>({
        proxy: proxies.sales,
        verb: Verb.POST,
        pathSegments: ['payments', 'grid'],
        payload: body,
      }).catch((err) => {
        throwBspError(err);
        return {
          results: [],
        };
      }),
    [throwBspError, callBsl]
  );

  const exportPaymentsGridData = async (body: PaymentsRequestDto): Promise<ExportFileReportGridResponse | void> => {
    const data: ExportFileReportGridResponse = await callBsl({
      proxy: proxies.sales,
      verb: Verb.POST,
      pathSegments: ['payments', 'grid', 'export'],
      payload: body,
    });
    if (data?.signedUrl) {
      downloadFile(data?.signedUrl);
    } else {
      throw new Error('signedUrl not found');
    }
    return data;
  };

  /**
   * Journal Entry
   */
  const getOneJournalEntry = useCallback(
    async (id: string): Promise<GetOneJournalEntryResponse | undefined> =>
      callBsl<GetOneJournalEntryResponse>({
        proxy: proxies.glAccounts,
        verb: Verb.GET,
        pathSegments: ['journal-entry', id],
      }).catch((err) => {
        throwBspError(err);
        return undefined;
      }),
    [throwBspError, callBsl]
  );

  const getAllJournalEntries = useCallback(
    async (body: GetAllJournalEntryRequest): Promise<GetAllJournalEntryResponse> =>
      callBsl<GetAllJournalEntryResponse>({
        proxy: proxies.glAccounts,
        verb: Verb.POST,
        pathSegments: ['journal-entry', 'get-all'],
        payload: body,
      }).catch((err) => {
        throwBspError(err);
        return { journalEntries: [] };
      }),
    [throwBspError, callBsl]
  );

  const createJournalEntry = useCallback(
    async (body: CreateJournalEntryRequest): Promise<JournalEntryId | undefined> =>
      callBsl<JournalEntryId>({
        proxy: proxies.glAccounts,
        verb: Verb.POST,
        pathSegments: ['journal-entry'],
        payload: body,
      }).catch((err) => {
        throwBspError(err);
        return undefined;
      }),
    [throwBspError, callBsl]
  );

  const updateJournalEntry = useCallback(
    async (body: Partial<CreateJournalEntryRequest>, id: string): Promise<JournalEntryId | undefined> =>
      callBsl<JournalEntryId>({
        proxy: proxies.glAccounts,
        verb: Verb.PUT,
        pathSegments: ['journal-entry', id],
        payload: body,
      }).catch((err) => {
        throwBspError(err);
        return undefined;
      }),
    [throwBspError, callBsl]
  );

  const deleteJournalEntry = useCallback(
    async (id: string): Promise<JournalEntryId | undefined> =>
      callBsl<JournalEntryId>({
        proxy: proxies.glAccounts,
        verb: Verb.DELETE,
        pathSegments: ['journal-entry', id],
      }).catch((err) => {
        throwBspError(err);
        return undefined;
      }),
    [throwBspError, callBsl]
  );

  const getOneRecurringJournalEntry = useCallback(
    async (id: string): Promise<GetOneJournalEntryResponse | undefined> =>
      callBsl<GetOneJournalEntryResponse>({
        proxy: proxies.glAccounts,
        verb: Verb.GET,
        pathSegments: ['journal-entry/recurring', id],
      }).catch((err) => {
        throwBspError(err);
        return undefined;
      }),
    [throwBspError, callBsl]
  );

  const getAllRecurringJournalEntries = useCallback(
    async (body: GetAllJournalEntryRequest): Promise<GetAllJournalEntryResponse> =>
      callBsl<GetAllJournalEntryResponse>({
        proxy: proxies.glAccounts,
        verb: Verb.POST,
        pathSegments: ['journal-entry/recurring', 'get-all'],
        payload: body,
      }).catch((err) => {
        throwBspError(err);
        return { journalEntries: [] };
      }),
    [throwBspError, callBsl]
  );

  const createRecurringJournalEntry = useCallback(
    async (body: CreateJournalEntryRequest): Promise<RecurringJournalEntryId | undefined> =>
      callBsl<RecurringJournalEntryId>({
        proxy: proxies.glAccounts,
        verb: Verb.POST,
        pathSegments: ['journal-entry/recurring'],
        payload: body,
      }).catch((err) => {
        throwBspError(err);
        return undefined;
      }),
    [throwBspError, callBsl]
  );

  const updateRecurringJournalEntry = useCallback(
    async (body: Partial<CreateJournalEntryRequest>, id: string): Promise<RecurringJournalEntryId | undefined> =>
      callBsl<RecurringJournalEntryId>({
        proxy: proxies.glAccounts,
        verb: Verb.PUT,
        pathSegments: ['journal-entry/recurring', id],
        payload: body,
      }).catch((err) => {
        throwBspError(err);
        return undefined;
      }),
    [throwBspError, callBsl]
  );

  const deleteRecurringJournalEntry = useCallback(
    async (id: string): Promise<RecurringJournalEntryId | undefined> =>
      callBsl<RecurringJournalEntryId>({
        proxy: proxies.glAccounts,
        verb: Verb.DELETE,
        pathSegments: ['journal-entry/recurring', id],
      }).catch((err) => {
        throwBspError(err);
        return undefined;
      }),
    [throwBspError, callBsl]
  );

  return {
    getOneJournalEntry,
    getAllJournalEntries,
    createJournalEntry,
    updateJournalEntry,
    deleteJournalEntry,
    getOneRecurringJournalEntry,
    getAllRecurringJournalEntries,
    createRecurringJournalEntry,
    updateRecurringJournalEntry,
    deleteRecurringJournalEntry,
    getHouseAccount,
    getHouseAccountsActivities,
    getHouseAccountsSummary,
    createHouseAccount,
    putHouseAccount,
    createStatement,
    createStatementPDF,
    deleteStatement,
    patchStatement,
    getStatement,
    patchHouseAccount,
    deleteHouseAccount,
    restoreHouseAccount,
    createTransaction,
    getTransaction,
    getSaleTransaction,
    getGuestTransaction,
    getAllGuestChecks,
    exportAllGuestChecks,
    createPdf,
    getKPIData,
    getDashboardGraphData,
    getSalesSummaryCardData,
    getKPIData2,
    getCalendarFilters,
    getPOSEventFilters,
    getPOSEvents,
    getProductMixMenuGridData,
    exportProductMixMenuGridData,
    getGlAccounts,
    getGlAccountFilters,
    deleteGlAccount,
    createGlAccount,
    getGlAccountUpsertInfo,
    getAllGlAccountNames,
    updateGlAccount,
    uploadGlAccounts,
    restoreDeleteGlAccount,
    updateIsActiveGlAccounts,
    getGlAccountById,
    getSalesDefinitionsSettings,
    updateSalesDefinitionsSettings,
    getComparativeSalesLaborGridData,
    getComparativeSalesLaborCardsData,
    exportComparativeSalesLaborPdf,
    exportComparativeSalesLaborCsv,
    getProductMixTop5Items,
    getProductMixTotalSales,
    getProductMixTop5Sellers,
    getProductMixEmployeeSalesGrid,
    exportProductMixEmployeeSalesGridData,
    getFilterValues,
    getDaypartFiltersBslRequest,
    getSalesSummaryGridData,
    exportSalesSummaryGridData,
    getSalesSummaryPaymentsGridData,
    exportSalesSummaryPaymentsGridData,
    getProfitLossGridData,
    getRefundsGridData,
    getRefundsCardsData,
    getTotalTaxesData,
    getTaxesGridData,
    getTaxesByTypeData,
    getDiscountReportKPICardsData,
    getDiscountReportTop5DiscountsData,
    getDiscountReportGridData,
    getSalesByRevenueCenterData,
    getPerPersonAverageByRevenueCenterData,
    getRevenueCenterGridData,
    getVoidsReportGridData,
    getVoidsReportKPICardsData,
    getVoidsReportTop5ReasonsData,
    getTotalPaymentsData,
    getPaymentsByTypeData,
    getPaymentsGridData,
    exportPaymentsGridData,
    getTotalSalesByShift,
  };
};

export default useSalesRequests;
